import { useEffect, useMemo, useState } from 'react'
import { PackageCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useExpressCompanies } from '@/hooks/useExpressCompany'
import type {
  EmployeeManualOrder,
  ManualOrderMarkRefundReturnedParams,
} from '@/api/employee-manual-order'

interface ManualOrderReturnDialogProps {
  order: EmployeeManualOrder | null
  open: boolean
  isSubmitting?: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: ManualOrderMarkRefundReturnedParams) => void
}

export function ManualOrderReturnDialog({
  order,
  open,
  isSubmitting = false,
  onOpenChange,
  onSubmit,
}: ManualOrderReturnDialogProps) {
  const [expressCode, setExpressCode] = useState('')
  const [expressNo, setExpressNo] = useState('')
  const [phone, setPhone] = useState('')
  const [explain, setExplain] = useState('')

  const { data: expressCompanies = [], isLoading: isExpressLoading, isError: isExpressError } = useExpressCompanies({
    enabled: open,
  })

  const expressName = useMemo(() => {
    const matched = expressCompanies.find((c) => c.code === expressCode)
    return matched?.name ?? ''
  }, [expressCompanies, expressCode])

  useEffect(() => {
    if (!open) {
      setExpressCode('')
      setExpressNo('')
      setPhone('')
      setExplain('')
    }
  }, [open])

  const canSubmit = expressName !== '' && expressNo.trim() !== '' && !isSubmitting && !isExpressError

  const handleSubmit = () => {
    if (!canSubmit) return

    onSubmit({
      refund_express: expressNo.trim(),
      refund_express_name: expressName,
      refund_phone: phone.trim() || undefined,
      refund_explain: explain.trim() || undefined,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageCheck className="h-4 w-4" />
            登记退货
          </DialogTitle>
          <DialogDescription>
            代用户填写退货物流，提交后售后单进入&quot;已退货&quot;状态。订单号：{order?.order_id ?? '-'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>快递公司</Label>
            <Select value={expressCode} onValueChange={setExpressCode} disabled={isExpressLoading || isExpressError || isSubmitting}>
              <SelectTrigger aria-label="快递公司">
                <SelectValue placeholder={isExpressLoading ? '加载中…' : isExpressError ? '物流公司加载失败' : '请选择快递公司'} />
              </SelectTrigger>
              <SelectContent>
                {expressCompanies.map((company) => (
                  <SelectItem key={company.code} value={company.code}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isExpressError && (
              <p className="text-xs text-destructive">物流公司加载失败，请稍后重试或刷新</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="return-express-no">快递单号</Label>
            <Input
              id="return-express-no"
              value={expressNo}
              onChange={(e) => setExpressNo(e.target.value)}
              placeholder="请输入退货快递单号"
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="return-phone">联系电话（选填）</Label>
            <Input
              id="return-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="退货联系电话"
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="return-explain">退货说明（选填）</Label>
            <Textarea
              id="return-explain"
              value={explain}
              onChange={(e) => setExplain(e.target.value)}
              placeholder="退货说明"
              rows={3}
              disabled={isSubmitting}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            取消
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting ? '提交中...' : '提交'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
